import React, { useState, useCallback } from 'react';

const GOLD = '#C9A84C';

/**
 * Shown on the home library host (Mac Mini) before the user leaves home.
 * macOS only displays the Local Network permission dialog when an app first
 * probes the LAN — this modal explains that step and re-triggers the probe.
 */
export default function HomeNetworkAccessModal({ open, onDismiss }) {
  const [busy, setBusy] = useState(false);

  const triggerPermission = useCallback(async () => {
    setBusy(true);
    try {
      const c = await import('../lib/connector');
      await c.warmLocalNetworkAccess();
    } catch (e) {
      console.warn('[HomeNetworkAccessModal] warm failed:', e);
    } finally {
      setBusy(false);
    }
  }, []);

  const acknowledge = useCallback(() => {
    try {
      localStorage.setItem('maestro_lan_setup_ack', Date.now().toString());
    } catch { /* ignore */ }
    onDismiss?.();
  }, [onDismiss]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10050,
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={acknowledge}
    >
      <div
        role="dialog"
        aria-labelledby="home-lan-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 440, width: '100%', borderRadius: 18, padding: '28px 26px',
          background: 'var(--bg-secondary, #1a1a1e)',
          border: '1px solid var(--border-light, rgba(255,255,255,0.08))',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        }}
      >
        <div id="home-lan-title" style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>
          Allow local network access
        </div>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 12, lineHeight: 1.55 }}>
          Before you leave home, macOS needs permission for Maestro to share your library with your
          other devices. When the system dialog appears, choose <strong>Allow</strong>.
        </p>
        <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginTop: 10, lineHeight: 1.5 }}>
          Do this now on this Mac — you won&apos;t be able to approve it remotely later.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
          <button
            type="button"
            disabled={busy}
            onClick={triggerPermission}
            style={{
              padding: '12px 18px', borderRadius: 12, border: 'none', cursor: busy ? 'wait' : 'pointer',
              background: GOLD, color: '#111', fontWeight: 700, fontSize: 14,
            }}
          >
            {busy ? 'Requesting…' : 'Show macOS Permission Dialog'}
          </button>
          <button
            type="button"
            onClick={acknowledge}
            style={{
              padding: '11px 18px', borderRadius: 12, cursor: 'pointer',
              background: 'transparent', border: '1px solid var(--border-light)',
              color: 'var(--text-secondary)', fontSize: 14, fontWeight: 600,
            }}
          >
            I&apos;ve allowed it — continue
          </button>
        </div>
      </div>
    </div>
  );
}
